import React, { useState } from 'react'
import { screenSizes } from 'shared/RandomUtils'
import useResizeListener from 'shared/Hooks/useResizeListener'
import { GridContainer } from 'employee-platform/shared/styles/Grid'
import AddTripForm from './components/AddTripForm'
import { Footer } from 'core-system/Program'
import styled from 'styled-components'
import Button from 'core-system/Button'
import { useLocation, useNavigate } from 'react-router-dom'
import { Locations } from 'shared/Router/Locations'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import employeePlatformService from 'redux/employeePlatform/employeePlatformService'
import Divider from 'core-system/Divider'
import { useDispatch, useSelector } from 'react-redux'
import { AppState } from 'redux/config/store'
import {
  buildTripPayload,
  initialTripFormValues,
  isTripSaveDisabled,
  TripFormLocationState,
} from './TripsUtils'

const FooterContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: row;
  justify-content: ${(props) => (props.isMobile ? 'center' : 'flex-end')};
  gap: 1rem;
`

const AddTripView = React.memo(() => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const locationState = useLocation().state as TripFormLocationState | null
  const trip = locationState?.trip
  const profileData = useSelector(
    (state: AppState) => state.employeePlatform.profileData
  )
  const initialForm = initialTripFormValues(
    locationState,
    profileData?.homeAddress,
    profileData?.worksiteAddress
  )

  const [isMobile, setIsMobile] = useState(
    window.innerWidth <= screenSizes.mobile
  )
  const handleResize = React.useCallback(
    (width: number) => {
      setIsMobile(width <= screenSizes.mobile)
    },
    [setIsMobile]
  )
  useResizeListener(() => handleResize(window.innerWidth), [])

  const [date, setDate] = useState(initialForm.date)
  const [timeRange, setTimeRange] = useState(initialForm.timeRange)
  const [source, setSource] = useState(initialForm.source)
  const [sourceAddr, setSourceAddr] = useState(initialForm.sourceAddr)
  const [dest, setDest] = useState(initialForm.dest)
  const [destAddr, setDestAddr] = useState(initialForm.destAddr)
  const [purpose, setPurpose] = useState<string | null>(initialForm.purpose)
  const [modeIds, setModeIds] = useState<string[]>(initialForm.modeIds)
  // const [noTripReason, setNoTripReason] = useState<string | null>(null)

  const addTripButtonDisabled = isTripSaveDisabled({
    date,
    timeRange,
    source,
    dest,
    modeIds,
  })

  const handleAddTrip = async () => {
    if (addTripButtonDisabled || !timeRange || !source || !dest) return
    const addTripPayload = buildTripPayload({
      date,
      timeRange,
      source,
      dest,
      purpose,
      modeIds,
    })
    if (trip) {
      try {
        await employeePlatformService.updateTrip({
          id: trip.id,
          ...addTripPayload,
        })
        dispatch(employeePlatformActions.updateTripSuccess())
      } catch {
        dispatch({ type: 'employeePlatform/updateTripFailed' })
        return
      }
    } else {
      dispatch(employeePlatformActions.addTrip(addTripPayload))
    }
    navigate(Locations.EmployeeTrips.Overview)
  }

  return (
    <GridContainer numColumns={1}>
      <AddTripForm
        isMobile={isMobile}
        date={date}
        setDate={setDate}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        source={source}
        setSource={setSource}
        sourceAddr={sourceAddr}
        setSourceAddr={setSourceAddr}
        dest={dest}
        setDest={setDest}
        destAddr={destAddr}
        setDestAddr={setDestAddr}
        purpose={purpose}
        setPurpose={setPurpose}
        modeIds={modeIds}
        setModeIds={setModeIds}
      />
      <Divider size='0%' marginY='2rem' />
      <Footer>
        <FooterContainer isMobile={isMobile}>
          {/* TODO: FUTURE - add selected month to trips overview URL and navigate back on cancel */}
          <Button
            variant='secondary'
            onClick={() => navigate(Locations.EmployeeTrips.Overview)}
          >
            Cancel
          </Button>
          <Button
            onClick={() => handleAddTrip()}
            disabled={addTripButtonDisabled}
          >
            {trip ? 'Update Trip' : 'Add Trip'}
          </Button>
        </FooterContainer>
      </Footer>
    </GridContainer>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  AddTripView.displayName = 'AddTripView'
}

export default AddTripView
